import {
  createMutationKeys,
  createQueryKeys,
  mergeQueryKeys,
} from '@lukemorales/query-key-factory'

import {
  IObservation,
  IObservationListItem,
  ObservationListStats,
} from '@core/entities/observation'
import { PageResponse } from '@core/entities/pagination'
import { s3Upload } from '@core/react-query/helpers/uploadHelpers'
import { createQueryKeyString } from '@core/react-query/queryParams'
import { APIAxiosInstance } from '@core/utils/api-axios-instance'
import { RequestTypes } from './requestTypes'

const observationQueryKeys = createQueryKeys('observation', {
  allObservations: ({
    organisationId,
    projectId,
    page,
    size,
    ...payload
  }: RequestTypes['getObservations']) => {
    return {
      queryKey: [organisationId, projectId, payload, page, size],
      queryFn: async ({ pageParam = 1 }) => {
        const queryParams = createQueryKeyString({
          organisationId,
          projectId,
          size,
          ...payload,
          page: page ? page : pageParam,
        })

        const { data } = await APIAxiosInstance.get(
          `observations${queryParams}`,
        )

        return data as PageResponse<IObservationListItem>
      },
    }
  },
  allObservationsWithStats: ({
    organisationId,
    projectId,
    page,
    size,
    ...payload
  }: RequestTypes['getObservations']) => {
    return {
      queryKey: [organisationId, projectId, payload, page, size],
      queryFn: async ({ pageParam = 1 }) => {
        const queryParams = createQueryKeyString({
          organisationId,
          projectId,
          size,
          ...payload,
          page: page ? page : pageParam,
        })

        const { data } = await APIAxiosInstance.get(
          `observations/all${queryParams}`,
        )

        const { items, stats } = data as {
          items: PageResponse<IObservationListItem>
          stats: ObservationListStats
        }

        return { ...items, stats }
      },
    }
  },
  byId: ({ observationId }: RequestTypes['getObservation']) => {
    return {
      queryKey: [observationId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `observations/${observationId}`,
        )

        return data as IObservation
      },
    }
  },
})

const observationMutationKeys = createMutationKeys('observation', {
  createObservation: {
    mutationKey: null,
    mutationFn: async ({
      idempotencyKey,
      lowFpsMedia,
      lowFpsValue,
      images,
      ...body
    }: RequestTypes['createObservation']) => {
      const amendedKey = lowFpsMedia
        ? await s3Upload({
            file: lowFpsMedia,
            folderName: 'observations',
          })
        : undefined

      const uploadedImages = images?.length
        ? await Promise.all(
            images.map((file) =>
              s3Upload({
                file,
                folderName: 'observations',
                coordinates: file.coordinates,
              }),
            ),
          )
        : undefined

      const media =
        amendedKey || uploadedImages
          ? {
              media: {
                ...(amendedKey ? { amendedKey, fps: lowFpsValue } : {}),
                ...(uploadedImages ? { images: uploadedImages } : {}),
              },
            }
          : {}

      const { data } = await APIAxiosInstance.post(
        'observations',
        {
          ...body,
          ...media,
        },
        idempotencyKey
          ? { headers: { 'X-IDEMPOTENCY-KEY': idempotencyKey } }
          : undefined,
      )

      return data as IObservation
    },
  },
  updateObservation: {
    mutationKey: null,
    mutationFn: async ({
      observationId,
      ...body
    }: RequestTypes['updateObservation']) => {
      const { data } = await APIAxiosInstance.put(
        `observations/${observationId}`,
        body,
      )

      return data
    },
  },
  rejectObservation: {
    mutationKey: null,
    mutationFn: async ({
      observationId,
      comment,
    }: RequestTypes['rejectObservation']) => {
      const { data } = await APIAxiosInstance.post(
        `observations/${observationId}/reject`,
        { comment },
      )

      return data
    },
  },
  closeObservation: {
    mutationKey: null,
    mutationFn: async ({
      observationId,
      comment,
    }: RequestTypes['closeObservation']) => {
      const { data } = await APIAxiosInstance.post(
        `observations/${observationId}/close`,
        { comment },
      )

      return data
    },
  },
  attachObservationMedia: {
    mutationKey: null,
    mutationFn: async ({
      observationId,
      media,
      idempotencyKey,
      signal,
    }: RequestTypes['attachObservationMedia']) => {
      const mediaKey = await s3Upload({
        file: media,
        folderName: 'observations',
        signal,
      })

      const { data } = await APIAxiosInstance.put(
        `observations/${observationId}/media`,
        { mediaKey },
        {
          headers: {
            ...(idempotencyKey ? { 'X-IDEMPOTENCY-KEY': idempotencyKey } : {}),
          },
          signal,
        },
      )

      return data as IObservation
    },
  },
})

const observationKeys = mergeQueryKeys(
  observationQueryKeys,
  observationMutationKeys,
).observation

export { observationKeys }
